import { atom } from 'jotai';

import { UserStatus } from '../_components/_shared/types';

import { settingsRouteParamAtom } from './settingsModule.state';

/**
 * User ID from route params
 */
export const $userId = settingsRouteParamAtom(
  ['/team-and-permissions/users/:userId'],
  'userId',
);

/**
 * Tab state for user details view
 */
export type UserDetailsTab =
  | 'contact-info'
  | 'preferences'
  | 'roles'
  | 'facilities';

export const $userDetailsTab = atom<UserDetailsTab>('contact-info');

/**
 * Current user being viewed/edited
 */
export interface CurrentUserData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  status: UserStatus;
  roles: string[];
  roleIds: string[];
  facilities: string[];
  facilityIds: string[];
  accessCode: string;
  createdAt: string;
}

export const $currentUser = atom<CurrentUserData | null>(null);

/**
 * User form state for editing
 */
export interface UserFormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  accessCode: string;
}

const initialFormState: UserFormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  accessCode: '',
};

export const $userForm = atom<UserFormState>(initialFormState);

export const $updateUserForm = atom(
  null,
  (get, set, update: Partial<UserFormState>) => {
    const currentState = get($userForm);
    set($userForm, {
      ...currentState,
      ...update,
    });
  },
);

export const $resetUserForm = atom(null, (get, set) => {
  set($userForm, initialFormState);
});

/**
 * Derived atom to check if the form has unsaved changes
 */
export const $isUserFormDirty = atom((get) => {
  const currentUser = get($currentUser);
  const form = get($userForm);

  if (!currentUser) return false;

  return (
    form.firstName !== currentUser.firstName ||
    form.lastName !== currentUser.lastName ||
    form.email !== currentUser.email ||
    form.phone !== (currentUser.phone ?? '') ||
    form.accessCode !== currentUser.accessCode
  );
});

/**
 * Initialize form from current user data
 */
export const $initUserForm = atom(null, (get, set) => {
  const currentUser = get($currentUser);
  if (currentUser) {
    set($userForm, {
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      email: currentUser.email,
      phone: currentUser.phone ?? '',
      accessCode: currentUser.accessCode,
    });
  }
});

/**
 * User preferences form state
 */
export interface UserPreferencesFormState {
  primaryFacilityId: string | null;
  primaryScaleId: string | null;
  defaultCashDrawerId: string | null;
  enableDefaultPrinting: boolean;
  skipScalePayment: boolean;
}

const initialPreferencesFormState: UserPreferencesFormState = {
  primaryFacilityId: null,
  primaryScaleId: null,
  defaultCashDrawerId: null,
  enableDefaultPrinting: true,
  skipScalePayment: true,
};

export const $userPreferencesForm = atom<UserPreferencesFormState>(
  initialPreferencesFormState,
);

export const $updateUserPreferencesForm = atom(
  null,
  (get, set, update: Partial<UserPreferencesFormState>) => {
    const currentState = get($userPreferencesForm);
    set($userPreferencesForm, {
      ...currentState,
      ...update,
    });
  },
);

export const $resetUserPreferencesForm = atom(null, (get, set) => {
  set($userPreferencesForm, initialPreferencesFormState);
});

/**
 * User location assignment state
 * Manages roles assigned to a user per location
 */
export interface UserFacilityAssignment {
  facilityId: string;
  facilityName: string;
  facilityAddress: string;
  assignedRoleIds: string[];
  lastUpdatedAt: string | null;
  lastUpdatedBy: string | null;
}

export interface RawAssignmentEntry {
  id: string;
  roleId: string;
  facilityId: string;
}

export interface UserFacilityFormState {
  assignments: UserFacilityAssignment[];
  selectedFacilityId: string | null;
  draftRoleIds: string[];
  isDirty: boolean;
  rawAssignments: RawAssignmentEntry[];
}

const initialFacilityFormState: UserFacilityFormState = {
  assignments: [],
  selectedFacilityId: null,
  draftRoleIds: [],
  isDirty: false,
  rawAssignments: [],
};

export const $userFacilityForm = atom<UserFacilityFormState>(
  initialFacilityFormState,
);

export const $updateUserFacilityForm = atom(
  null,
  (get, set, update: Partial<UserFacilityFormState>) => {
    const currentState = get($userFacilityForm);
    set($userFacilityForm, {
      ...currentState,
      ...update,
    });
  },
);

export const $resetUserFacilityForm = atom(null, (get, set) => {
  set($userFacilityForm, initialFacilityFormState);
});

export const $selectFacility = atom(
  null,
  (get, set, facilityId: string | null) => {
    const state = get($userFacilityForm);
    const assignment = state.assignments.find(
      (a) => a.facilityId === facilityId,
    );

    set($userFacilityForm, {
      ...state,
      selectedFacilityId: facilityId,
      draftRoleIds: assignment?.assignedRoleIds ?? [],
      isDirty: false,
    });
  },
);

export const $updateDraftRoles = atom(null, (get, set, roleIds: string[]) => {
  const state = get($userFacilityForm);
  const assignment = state.assignments.find(
    (a) => a.facilityId === state.selectedFacilityId,
  );
  const originalRoleIds = assignment?.assignedRoleIds ?? [];

  // Check if dirty by comparing arrays
  const isDirty =
    roleIds.length !== originalRoleIds.length ||
    roleIds.some((id) => !originalRoleIds.includes(id));

  set($userFacilityForm, {
    ...state,
    draftRoleIds: roleIds,
    isDirty,
  });
});

export const $saveDraftToAssignments = atom(null, (get, set) => {
  const state = get($userFacilityForm);
  if (!state.selectedFacilityId) return;

  const updatedAssignments = state.assignments.map((assignment) => {
    if (assignment.facilityId === state.selectedFacilityId) {
      return {
        ...assignment,
        assignedRoleIds: state.draftRoleIds,
        lastUpdatedAt: new Date().toISOString(),
        lastUpdatedBy: 'Current User', // TODO: Get from auth context
      };
    }
    return assignment;
  });

  set($userFacilityForm, {
    ...state,
    assignments: updatedAssignments,
    isDirty: false,
  });
});

export const $discardDraftChanges = atom(null, (get, set) => {
  const state = get($userFacilityForm);
  const assignment = state.assignments.find(
    (a) => a.facilityId === state.selectedFacilityId,
  );

  set($userFacilityForm, {
    ...state,
    draftRoleIds: assignment?.assignedRoleIds ?? [],
    isDirty: false,
  });
});

// =============================================================================
// User Role Assignment State
// Manages facilities assigned to a user per role
// =============================================================================

export interface UserRoleAssignment {
  roleId: string;
  roleName: string;
  userCount: number;
  assignedFacilityIds: string[];
  lastUpdatedAt: string | null;
  lastUpdatedBy: string | null;
}

export interface UserRoleFormState {
  assignments: UserRoleAssignment[];
  selectedRoleId: string | null;
  draftFacilityIds: string[];
  isDirty: boolean;
  rawAssignments: RawAssignmentEntry[];
}

const initialRoleFormState: UserRoleFormState = {
  assignments: [],
  selectedRoleId: null,
  draftFacilityIds: [],
  isDirty: false,
  rawAssignments: [],
};

export const $userRoleForm = atom<UserRoleFormState>(initialRoleFormState);

export const $updateUserRoleForm = atom(
  null,
  (get, set, update: Partial<UserRoleFormState>) => {
    const currentState = get($userRoleForm);
    set($userRoleForm, {
      ...currentState,
      ...update,
    });
  },
);

export const $resetUserRoleForm = atom(null, (get, set) => {
  set($userRoleForm, initialRoleFormState);
});

export const $selectRole = atom(null, (get, set, roleId: string | null) => {
  const state = get($userRoleForm);
  const assignment = state.assignments.find((a) => a.roleId === roleId);

  set($userRoleForm, {
    ...state,
    selectedRoleId: roleId,
    draftFacilityIds: assignment?.assignedFacilityIds ?? [],
    isDirty: false,
  });
});

export const $updateDraftFacilities = atom(
  null,
  (get, set, facilityIds: string[]) => {
    const state = get($userRoleForm);
    const assignment = state.assignments.find(
      (a) => a.roleId === state.selectedRoleId,
    );
    const originalFacilityIds = assignment?.assignedFacilityIds ?? [];

    const isDirty =
      facilityIds.length !== originalFacilityIds.length ||
      facilityIds.some((id) => !originalFacilityIds.includes(id));

    set($userRoleForm, {
      ...state,
      draftFacilityIds: facilityIds,
      isDirty,
    });
  },
);

export const $saveRoleDraftToAssignments = atom(null, (get, set) => {
  const state = get($userRoleForm);
  if (!state.selectedRoleId) return;

  const updatedAssignments = state.assignments.map((assignment) => {
    if (assignment.roleId === state.selectedRoleId) {
      return {
        ...assignment,
        assignedFacilityIds: state.draftFacilityIds,
        lastUpdatedAt: new Date().toISOString(),
        lastUpdatedBy: 'Current User',
      };
    }
    return assignment;
  });

  set($userRoleForm, {
    ...state,
    assignments: updatedAssignments,
    isDirty: false,
  });
});

export const $discardRoleDraftChanges = atom(null, (get, set) => {
  const state = get($userRoleForm);
  const assignment = state.assignments.find(
    (a) => a.roleId === state.selectedRoleId,
  );

  set($userRoleForm, {
    ...state,
    draftFacilityIds: assignment?.assignedFacilityIds ?? [],
    isDirty: false,
  });
});
